import styles from './invoiceSaleOperationForm.module.css'
import { Button, Validatable } from '../../../../../features/ui'
import { Labels } from '../../../../../features/data-types'
import { addressHelpers } from '../../../../../features/address'
import { Provincia } from '../../../../../services/gob-ar'
import { TaxpayerDataBody } from '../../../../../services/server'
import { toList } from '../../../../../utils/listUtils'
import { VatCondition } from '../../../state/types'
import { findProvinceName, TaxPayerLookupEvaluation } from './taxPayerLookup'

export type TaxPayerFeedback =
    | { type: 'success'; message: string }
    | { type: 'warning'; message: string }
    | { type: 'suggestion'; data: TaxpayerDataBody; evaluation: TaxPayerLookupEvaluation }

type Props = {
    feedback: TaxPayerFeedback
    currentName?: string
    currentVatCondition?: VatCondition
    provinces: Provincia[]
    onApply: () => void
    onDiscard: () => void
}

const missingLabels = (evaluation: TaxPayerLookupEvaluation): string[] => [
    ...(evaluation.name.missing ? ['El padrón no devolvió el nombre.'] : []),
    ...(evaluation.vatCondition.missing ? ['El padrón no devolvió la condición de IVA.'] : []),
    ...(evaluation.address.missing ? ['El padrón no devolvió el domicilio.'] : []),
]

const ComparisonRow = (props: { label: string; padron?: string; current?: string; showCurrent?: boolean }) => {
    const { label, padron, current, showCurrent = true } = props
    return <div className={styles.taxPayerComparisonRow}>
        <span className={styles.taxPayerComparisonLabel}>{label}</span>
        <span className={styles.taxPayerComparisonPadron}>{padron || '-'}</span>
        {showCurrent && (
            <span className={styles.taxPayerComparisonCurrent}>{current ? `Actual: ${current}` : 'Vacío'}</span>
        )}
    </div>
}

export const TaxPayerLookupFeedback = (props: Props) => {
    const { feedback, currentName, currentVatCondition, provinces, onApply, onDiscard } = props

    const { describeAddress } = addressHelpers

    if (feedback.type === 'success') {
        return <div className={styles.taxPayerSuccess}>{feedback.message}</div>
    }

    if (feedback.type === 'warning') {
        return <Validatable type="warning" validations={toList(feedback.message)} expanded />
    }

    const { data, evaluation } = feedback
    const missing = missingLabels(evaluation)
    const showName = evaluation.name.differing || evaluation.name.empty
    const showVatCondition = evaluation.vatCondition.differing || evaluation.vatCondition.empty
    const showAddress = evaluation.address.differing || evaluation.address.empty

    const padronProvince = findProvinceName(data.address?.province, provinces) ?? data.address?.province
    const padronAddress = data.address
        ? (data.address.street
            ? describeAddress({
                address: data.address.street,
                locality: data.address.city,
                province: padronProvince
            })
            : [data.address.city, padronProvince].filter(Boolean).join(', ') || undefined)
        : undefined

    return <div className={styles.taxPayerFeedback}>
        {missing.length > 0 && (
            <Validatable type="warning" validations={missing} expanded />
        )}
        <div className={styles.taxPayerComparison}>
            {showName && (
                <ComparisonRow
                    label="Nombre / Razón Social"
                    padron={data.name}
                    current={currentName}
                />
            )}
            {showVatCondition && (
                <ComparisonRow
                    label="Condición de IVA"
                    padron={data.vatCondition ? Labels.vatConditionLabel(data.vatCondition) : undefined}
                    current={currentVatCondition ? Labels.vatConditionLabel(currentVatCondition) : undefined}
                />
            )}
            {showAddress && (
                <ComparisonRow
                    label="Domicilio"
                    padron={padronAddress}
                    showCurrent={false}
                />
            )}
        </div>
        <div className={styles.taxPayerActions}>
            <Button
                label="Usar datos del padrón"
                kind="tertiary"
                icon="check-lg"
                small
                onClick={onApply}
            />
            <Button
                label="Descartar"
                kind="secondary"
                small
                onClick={onDiscard}
            />
        </div>
    </div>
}
